import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../AuthProvider';
import './Interview.css';
import behavioralQuestionsBank from '../data/behavioralQuestions'; // Import the questions bank
import technicalQuestionsBank from '../data/technicalQuestions'; // Import the technical questions bank

const Interview = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { interviewId } = useParams(); // Ensure this is correctly set in your routing

  const [selfIntro, setSelfIntro] = useState(''); // State for user's self-introduction
  const [followUpQuestion, setFollowUpQuestion] = useState(''); // State for follow-up question
  const [transcript, setTranscript] = useState([]); // State for the interview transcript
  const [introduction, setIntroduction] = useState(''); // State for the interview introduction
  const [audioUrl, setAudioUrl] = useState(''); // State for the audio URL
  const [followUpAudioUrl, setFollowUpAudioUrl] = useState(''); // State for the follow-up audio URL
  const [isAudioReady, setIsAudioReady] = useState(false); // State to track if audio is ready
  const [isInterviewStarted, setIsInterviewStarted] = useState(false); // State to track if interview has started
  const [behavioralQuestionsCount, setBehavioralQuestionsCount] = useState(1);
  const [mode, setMode] = useState('voice');
  const [selectedBehavioralQuestions, setSelectedBehavioralQuestions] = useState([]); // Selected questions
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0); // Current question
  const [isRecording, setIsRecording] = useState(false); // State to track if recording is in progress
  const [liveTranscription, setLiveTranscription] = useState(''); // State for live transcription
  const [currentQuestion, setCurrentQuestion] = useState('');
  const [isAnswering, setIsAnswering] = useState(false);
  const [technicalQuestions, setTechnicalQuestions] = useState({
    productDesign: false,
    marketSizing: false,
    metrics: false
  });
  const [showFinishButton, setShowFinishButton] = useState(false);
  const [selectedTechnicalQuestions, setSelectedTechnicalQuestions] = useState([]); // Selected technical questions

  const [error, setError] = useState('');

  // Replace this line:
  // const [currentTechnicalQuestionIndex, setCurrentTechnicalQuestionIndex] = useState(0);
  
  // With this:
  const currentTechnicalQuestionIndexRef = useRef(0);

  const technicalQuestionTimes = {
    productDesign: 10,
    marketSizing: 7,
    metrics: 6
  };

  const [introductionFetched, setIntroductionFetched] = useState(false);
  const [isLoading, setIsLoading] = useState(false); // New state for loading

  const [userPlan, setUserPlan] = useState('free');
  const [interviewCount, setInterviewCount] = useState(0);

  useEffect(() => {
    const fetchUserData = async () => {
      if (user) {
        const token = await user.getIdToken();
        const response = await fetch('/get-user-data', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (response.ok) {
          const data = await response.json();
          setUserPlan(data.plan);
          setInterviewCount(data.interviewCount);
        }
      }
    };

    fetchUserData();
  }, [user]);

  const calculateTotalTime = () => {
    const behavioralTime = behavioralQuestionsCount * 2;
    const technicalTime = Object.keys(technicalQuestions).reduce((total, question) => {
      return total + (technicalQuestions[question] ? technicalQuestionTimes[question] : 0);
    }, 0);
    return behavioralTime + technicalTime;
  };

  const audioRef = useRef(null); // Ref for the audio element
  const followUpAudioRef = useRef(null); // Ref for the follow-up audio element
  const mediaRecorderRef = useRef(null); // Ref for the MediaRecorder
  const recordedChunksRef = useRef([]);

  // Function to select random questions from the bank
  const selectBehavioralQuestions = (count) => {
    const shuffled = [...behavioralQuestionsBank].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, count).map(item => item.question); // Extract the 'question' field
  };

  // Function to select random technical questions from the bank
  const selectTechnicalQuestions = (categories) => {
    const selectedQuestions = [];
    categories.forEach(category => {
      const questions = technicalQuestionsBank[category];
      if (questions && questions.length > 0) {
        const shuffled = [...questions].sort(() => 0.5 - Math.random());
        selectedQuestions.push(...shuffled.slice(0, 1)); // Select one question per category
      }
    });
    return selectedQuestions.map(item => item.question); // Extract the 'question' field
  };

  const addToTranscript = (role, text) => {
    setTranscript((prev) => [...prev, { role, text }]);
  };

  // Modified playAudio function using addEventListener for better reliability
  const playAudio = (url, onEndedCallback) => {
    if (audioRef.current) {
      // Remove any existing 'ended' event listeners to prevent multiple triggers
      audioRef.current.removeEventListener('ended', audioRef.current.onended);
      
      audioRef.current.src = url;
      audioRef.current.preload = 'auto';
      audioRef.current.load();

      // Define a handler to wrap the callback and ensure it's only called once
      const handleEnded = () => {
        onEndedCallback();
        audioRef.current.removeEventListener('ended', handleEnded); // Clean up
      };

      audioRef.current.addEventListener('ended', handleEnded);

      const playPromise = audioRef.current.play();

      if (playPromise !== undefined) {
        playPromise.catch(error => {
          console.error('Error playing audio:', error);
        });
      }
    }
  };

  useEffect(() => {
    const fetchIntroduction = async () => {
      if (introductionFetched) return; // Skip if already fetched

      try {
        const response = await fetch('https://api.openai.com/v1/chat/completions', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}`,
          },
          body: JSON.stringify({
            model: 'gpt-4o',
            messages: [
              { 
                role: 'system', 
                content: `You are a Product Manager who is interviewing a candidate named ${user.displayName} for a product manager role. Provide a 10-second introduction to the interview and finish by asking the interviewee to "tell me about yourself".` 
              },
            ],
            max_tokens: 150,
          }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        const introText = data.choices[0].message.content.trim();
        setIntroduction(introText);

        // Fetch the audio from OpenAI TTS API
        const ttsResponse = await fetch('https://api.openai.com/v1/audio/speech', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}`,
          },
          body: JSON.stringify({
            model: 'tts-1',
            voice: 'alloy',
            input: introText,
          }),
        });

        if (!ttsResponse.ok) {
          throw new Error(`HTTP error! status: ${ttsResponse.status}`);
        }

        const audioBlob = await ttsResponse.blob();
        const audioUrl = URL.createObjectURL(audioBlob);
        setAudioUrl(audioUrl);
        setIsAudioReady(true);
        setIntroductionFetched(true); // Mark as fetched

      } catch (error) {
        console.error('Error fetching introduction or TTS audio:', error);
      }
    };

    if (!introductionFetched) {
      fetchIntroduction();
    }
  }, [user.displayName, introductionFetched]);

  const startQuestionFlow = () => {
    const selectedBehavioral = selectBehavioralQuestions(behavioralQuestionsCount);
    const selectedTechnical = selectTechnicalQuestions(Object.keys(technicalQuestions).filter(key => technicalQuestions[key]));
    setSelectedBehavioralQuestions([...selectedBehavioral]);
    setSelectedTechnicalQuestions(selectedTechnical);
    setCurrentQuestionIndex(0);
    currentTechnicalQuestionIndexRef.current = 0;
    
    // Add the introduction to the transcript here
    addToTranscript('interviewer', introduction);
  };

  const askQuestion = async (question) => {
    setCurrentQuestion(question);
    try {
      let url;

      if (question === introduction) {
        // Use the pre-fetched audio URL for the introduction
        url = audioUrl;
      } else {
        // Fetch TTS audio for other questions
        const ttsResponse = await fetch('https://api.openai.com/v1/audio/speech', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}`,
          },
          body: JSON.stringify({
            model: 'tts-1',
            voice: 'alloy',
            input: question,
          }),
        });

        if (!ttsResponse.ok) {
          throw new Error(`HTTP error! status: ${ttsResponse.status}`);
        }

        const audioBlob = await ttsResponse.blob();
        url = URL.createObjectURL(audioBlob);
      }

      setAudioUrl(url);
      setIsAnswering(true);

      // Only add to transcript if it's not the introduction
      if (question !== introduction) {
        addToTranscript('interviewer', question);
      }

      // Play the audio and start recording when it ends
      playAudio(url, () => {
        startRecording();
      });

    } catch (error) {
      console.error('Error fetching question TTS audio:', error);
    }
  };

  const startRecording = () => {
    // Stop any existing recorder
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    
    // Clear previous chunks synchronously
    recordedChunksRef.current = [];
    
    navigator.mediaDevices.getUserMedia({ audio: true })
      .then(stream => {
        const mediaRecorder = new MediaRecorder(stream);
        mediaRecorderRef.current = mediaRecorder;
        mediaRecorder.start(1000); // Record in 1-second chunks
        setIsRecording(true);

        mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            recordedChunksRef.current.push(event.data);
          }
        };

        mediaRecorder.onstop = () => {
          setIsRecording(false);
          processRecording();
          // Stop all tracks in the stream to release the microphone
          stream.getTracks().forEach(track => track.stop());
        };
      })
      .catch(error => {
        console.error('Error accessing microphone:', error);
        // Handle errors
      });
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      // No need to call processRecording() here as it's called in mediaRecorder.onstop
    }
  };

  const processRecording = async () => {
    const blob = new Blob(recordedChunksRef.current, { type: 'audio/webm' });
    const formData = new FormData();
    formData.append('file', blob, 'recording.webm');
    formData.append('model', 'whisper-1');

    try {
      const response = await fetch('https://api.openai.com/v1/audio/transcriptions', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Error response from server:', errorData);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const transcription = data.text.trim();
      
      addToTranscript('user', transcription);

      // Reset recordedChunksRef after processing
      recordedChunksRef.current = [];
      mediaRecorderRef.current = null; // Reset the MediaRecorder

      console.log('Current Question Index:', currentQuestionIndex);
      console.log('Selected Behavioral Questions Length:', selectedBehavioralQuestions.length);
      console.log('Current Technical Question Index:', currentTechnicalQuestionIndexRef.current);
      console.log('Selected Technical Questions Length:', selectedTechnicalQuestions.length);

      if (currentQuestionIndex < selectedBehavioralQuestions.length - 1) {
        // Still have behavioral questions
        const newIndex = currentQuestionIndex + 1;
        setCurrentQuestionIndex(newIndex);
        await fetchFollowUpResponse(transcription);
      } else if (currentQuestionIndex === selectedBehavioralQuestions.length - 1) {
        // Finished last behavioral question
        if (selectedTechnicalQuestions.length > 0) {
          setCurrentQuestionIndex(currentQuestionIndex + 1);
          currentTechnicalQuestionIndexRef.current = 0;
          await askTechnicalQuestion(0);
        } else {
          endInterview();
        }
      } else {
        const techIndex = currentTechnicalQuestionIndexRef.current;
        if (techIndex < selectedTechnicalQuestions.length - 1) {
          currentTechnicalQuestionIndexRef.current = techIndex + 1;
          await askTechnicalQuestion(techIndex + 1);
        } else {
          // This is the last technical question
          endInterview();
        }
      }
    } catch (error) {
      console.error('Error processing recording:', error);
      setIsRecording(false); // Ensure recording state is false in case of error
    }
  };

  const fetchFollowUpResponse = async (transcription) => {
    try {
      const nextQuestion = selectedBehavioralQuestions[currentQuestionIndex + 1];
      const gptResponse = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}`,
        },
        body: JSON.stringify({
          model: 'gpt-4o',
          messages: [
            { 
              role: 'system', 
              content: `You are a product manager interviewing a candidate for a product manager role. The candidate has just answered the question "${currentQuestion}". The next question you want to ask is "${nextQuestion}". Based on their response and the next question, generate a smooth transition and ask the next question.` 
            },
            { role: 'user', content: `Candidate's response: "${transcription}"` }
          ],
          max_tokens: 300,
        }),
      });

      if (!gptResponse.ok) {
        throw new Error(`HTTP error! status: ${gptResponse.status}`);
      }

      const gptData = await gptResponse.json();
      const followUp = gptData.choices[0].message.content.trim();
      setFollowUpQuestion(followUp);
      addToTranscript('interviewer', followUp);

      // Fetch the audio for the follow-up question
      const ttsResponse = await fetch('https://api.openai.com/v1/audio/speech', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}`,
        },
        body: JSON.stringify({
          model: 'tts-1',
          voice: 'alloy',
          input: followUp,
        }),
      });

      if (!ttsResponse.ok) {
        throw new Error(`HTTP error! status: ${ttsResponse.status}`);
      }

      const audioBlob = await ttsResponse.blob();
      const url = URL.createObjectURL(audioBlob);
      setAudioUrl(url);
      setFollowUpAudioUrl(url);

      // Play the audio and start recording when it ends
      if (audioRef.current) {
        audioRef.current.src = url;
        audioRef.current.play().catch(error => {
          console.error('Error playing audio:', error);
        });
        audioRef.current.onended = () => {
          startRecording();
        };
      }

    } catch (error) {
      console.error('Error processing follow-up response:', error);
    }
  };

  const handleTechnicalQuestionChange = (event) => {
    const { name, checked } = event.target;
    setTechnicalQuestions(prev => ({ ...prev, [name]: checked }));
  };

  const handleStartInterview = () => {
    if (userPlan === 'free' && interviewCount >= 1) {
      setError('Free accounts are limited to one mock interview. Upgrade to Pro for unlimited interviews!');
      return;
    }

    if (!isAudioReady) {
      console.error('Audio is not ready yet.');
      return;
    }

    setIsLoading(true);
    setIsInterviewStarted(true);
    startQuestionFlow();

    if (audioUrl) {
      playAudio(audioUrl, () => {
        console.log('Introduction audio ended. Starting recording...');
        startRecording();
        setIsLoading(false);
      });
    } else {
      console.error('Audio URL for introduction is not available.');
      setIsLoading(false);
    }
  };

  const handleFinish = async () => {
    if (user) {
      try {
        const token = await user.getIdToken();
        const payload = {
          id: interviewId,
          date: new Date().toISOString(),
          transcript,
          behavioralQuestions: selectedBehavioralQuestions,
          mode,
          technicalQuestions: Object.keys(technicalQuestions).filter(key => technicalQuestions[key])
        };

        const response = await fetch('http://localhost:4242/add-interview', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        });

        if (response.ok) {
          navigate(`/feedback/${interviewId}`);
        } else {
          const errorData = await response.json();
          console.error('Failed to save interview transcript', response.status, response.statusText, errorData);
        }
      } catch (error) {
        console.error('Error during interview submission:', error);
      }
    } else {
      console.error('User is not authenticated.');
    }
  };

  // New function to handle audio playback
  const playQuestionAudio = (question) => {
    if (audioRef.current) {
      audioRef.current.src = audioUrl;
      audioRef.current.preload = 'auto'; // Ensure audio is preloaded
      audioRef.current.load(); // Explicitly load the audio
      
      audioRef.current.play().catch(error => {
        console.error('Error playing audio:', error);
      });

      audioRef.current.onended = () => {
        if (question === introduction) {
          startRecording();
        } else {
          // For subsequent questions, you might want to add additional logic here
        }
      };
    }
  };

  const askTechnicalQuestion = async (index) => {
    console.log('Asking Technical Question:', index);
    if (index < selectedTechnicalQuestions.length) {
      let question = selectedTechnicalQuestions[index];
      
      // Add transition text if it's the first technical question
      if (index === 0) {
        question = `Thank you for answering the behavioral questions. Let's move on to the technical questions. ${question}`;
      }

      setCurrentQuestion(question);
      try {
        const ttsResponse = await fetch('https://api.openai.com/v1/audio/speech', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}`,
          },
          body: JSON.stringify({
            model: 'tts-1',
            voice: 'alloy',
            input: question,
          }),
        });

        if (!ttsResponse.ok) {
          throw new Error(`HTTP error! status: ${ttsResponse.status}`);
        }

        const audioBlob = await ttsResponse.blob();
        const url = URL.createObjectURL(audioBlob);
        setAudioUrl(url);
        setIsAnswering(true);
        addToTranscript('interviewer', question);

        playAudio(url, () => {
          startRecording();
        });
      } catch (error) {
        console.error('Error fetching technical question TTS audio:', error);
      }
    } else {
      console.log('No more technical questions, ending interview');
      endInterview();
    }
  };

  const endInterview = async () => {
    // Stop any ongoing recording
    if (isRecording) {
      stopRecording();
    }

    const finalMessage = "Great, those are all the questions I had for you today. Thank you for interviewing with us.";
    addToTranscript('interviewer', finalMessage);

    try {
      const ttsResponse = await fetch('https://api.openai.com/v1/audio/speech', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}`,
        },
        body: JSON.stringify({
          model: 'tts-1',
          voice: 'alloy',
          input: finalMessage,
        }),
      });

      if (!ttsResponse.ok) {
        throw new Error(`HTTP error! status: ${ttsResponse.status}`);
      }

      const audioBlob = await ttsResponse.blob();
      const url = URL.createObjectURL(audioBlob);
      setAudioUrl(url);

      // Use playAudio with the appropriate onended handler
      playAudio(url, () => {
        setShowFinishButton(true);
        setIsRecording(false); // Ensure recording state is false
      });

    } catch (error) {
      console.error('Error fetching final message TTS audio:', error);
      setShowFinishButton(true); // Show finish button even if audio fails
      setIsRecording(false); // Ensure recording state is false
    }
  };

  // Optional: Add useEffect to monitor isAudioReady and handle edge cases
  useEffect(() => {
    if (isAudioReady && isInterviewStarted && audioUrl) {
      // Ensure that audio is played only once when it's ready and interview has started
      playAudio(audioUrl, () => {
        console.log('Introduction audio ended. Starting recording...');
        startRecording();
      });
    }
    // Adding dependencies to prevent multiple triggers
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAudioReady, isInterviewStarted, audioUrl]);

  return (
    <div className="interview-container">
      {!isInterviewStarted ? (
        <div className="interview-setup-container">
          <h2>Set Up Your Interview</h2>
          {error && <div className="error-message">{error}</div>}
          <form>
            <label>
              Behavioral Questions:
              <select value={behavioralQuestionsCount} onChange={(e) => setBehavioralQuestionsCount(Number(e.target.value))}>
                {[...Array(6).keys()].map((num) => (
                  <option key={num} value={num + 1}>{num + 1}</option>
                ))}
              </select>
              <span>est. time is {behavioralQuestionsCount * 2} minutes</span>
            </label>
            
            <div>
              <h3>Technical Questions:</h3>
              <label>
                <input
                  type="checkbox"
                  name="productDesign"
                  checked={technicalQuestions.productDesign}
                  onChange={handleTechnicalQuestionChange}
                />
                Product Design (est. 10 minutes)
              </label>
              <label>
                <input
                  type="checkbox"
                  name="marketSizing"
                  checked={technicalQuestions.marketSizing}
                  onChange={handleTechnicalQuestionChange}
                />
                Market Sizing (est. 7 minutes)
              </label>
              <label>
                <input
                  type="checkbox"
                  name="metrics"
                  checked={technicalQuestions.metrics}
                  onChange={handleTechnicalQuestionChange}
                />
                Metrics (est. 6 minutes)
              </label>
            </div>
            
            <p>Total estimated time: {calculateTotalTime()} minutes</p>
            
            <button
              type="button"
              onClick={handleStartInterview}
              disabled={isLoading || !isAudioReady} // Disable based on loading and audio readiness
            >
              {isLoading ? 'Loading...' : "Let's Get Started!"} {/* Show loading text */}
            </button>
          </form>
        </div>
      ) : (
        <div>
          <h2>Interview</h2>
          {audioUrl && <audio ref={audioRef} src={audioUrl} controls />}
          <div>
            {/* Conditionally render Stop Recording button only if recording is active and interview hasn't ended */}
            {isRecording && !showFinishButton && (
              <button onClick={stopRecording}>Stop Recording</button>
            )}
            {transcript.length > 0 && (
              <div className="transcript">
                {transcript.map((entry, index) => (
                  <div key={index} className={`transcript-entry ${entry.role}`}>
                    <strong>{entry.role}:</strong> {entry.text}
                  </div>
                ))}
              </div>
            )}
          </div>
          {showFinishButton && (
            <button type="button" onClick={handleFinish}>Finish Interview</button>
          )}
        </div>
      )}
    </div>
  );
};

export default Interview;